import Icon from "@src/straps/base/icons/Icon/Icon";
import { IconNames } from "@src/straps/base/icons/Icon/types";
import { ClickableDiv } from "@src/straps/utils/ClickableDiv";
import classNames from "classnames";
import { ComponentProps, FunctionComponent } from "react";
import { useRouteMatch } from "react-router-dom";
import LinkWithQuery from "@src/straps/base/navigation/LinkWithQuery/LinkWithQuery";
import Text from "../../type/Text/Text";

export const ROW_WITH_SEPARATOR_HEIGHT = 42;
const ROW_NO_SEPARATOR_HEIGHT = 34;

const levelClassNameMap: { [level: number]: string } = {
  0: "pl-2.5",
  1: "pl-2.5",
  2: "pl-[39px]",
  3: "pl-[60px]",
  4: "pl-[80px]",
  5: "pl-[100px]",
};

const ChildIndicator = () => (
  <svg
    width="9"
    height="9"
    viewBox="0 0 9 9"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M0.5 0V8H8.5" stroke="#9E9E9E" />
  </svg>
);

const getTextColor = ({
  disabled,
  isChainActive,
}: {
  disabled: boolean;
  isChainActive: boolean;
}) => {
  if (disabled) {
    return "secondary";
  }
  if (isChainActive) {
    return "accent-2";
  }
  return "primary";
};

const getCaretColor = ({
  isExpanded = false,
  isChainActive = false,
}: {
  isExpanded?: boolean;
  isChainActive?: boolean;
}): ComponentProps<typeof Icon>["color"] => {
  if (isChainActive) {
    return "accent-2";
  }
  if (isExpanded) {
    return "primary";
  }
  return "secondary";
};

function getHeight({ level }: { level: number }) {
  if (level === 0) {
    return ROW_WITH_SEPARATOR_HEIGHT;
  }
  return ROW_NO_SEPARATOR_HEIGHT;
}

export const LeftNavItemRowTitle: FunctionComponent<
  React.PropsWithChildren<{
    isChainActive?: boolean;
    disabled?: boolean;
  }>
> = ({ isChainActive = false, disabled = false, children }) => {
  return (
    <Text
      variant="sb_t-14-500"
      color={getTextColor({ disabled, isChainActive })}
      className="grow overflow-hidden text-ellipsis"
    >
      {children}
    </Text>
  );
};

interface LeftNavItemRowProps {
  title: string;
  titleElement?: React.ReactElement; // Takes precedence over title. title is still used for aria label.
  path?: string;
  hasChildren?: boolean;
  setExpanded?: (isExpanded: boolean) => void;
  isExpanded?: boolean;
  level: number;
  disabled?: boolean;
  isLeftNavCollapsed: boolean;
  iconName?: IconNames;
  exact?: boolean;
  onClick?: () => void;
  persistQueries?: string[];
  isChainActiveOverride?: boolean; // Used when the row has no path
}
export const LeftNavItemRow: FunctionComponent<
  React.PropsWithChildren<LeftNavItemRowProps>
> = ({
  title,
  titleElement,
  path,
  hasChildren = false,
  setExpanded,
  isExpanded,
  level,
  disabled = false,
  isLeftNavCollapsed,
  iconName,
  exact,
  onClick,
  persistQueries = [],
  isChainActiveOverride = false,
}) => {
  const exactMatch = useRouteMatch({
    path: path,
    exact: hasChildren || path === "/" || exact,
  });
  const match = useRouteMatch({
    path: path,
    exact: path === "/" || exact, // The root path would otherwise always be matched
  });
  const isActive = Boolean(exactMatch) && !hasChildren; // True only if self is active. Only leaf nodes can be active.
  const isChainActive = Boolean(match) || isChainActiveOverride; // True if self or any descendents are active
  const levelClassName = levelClassNameMap[level];
  const textColor = getTextColor({
    disabled,
    isChainActive,
  });
  const caretColor = getCaretColor({
    isExpanded,
    isChainActive,
  });
  const isTopLevelAndLeftNavCollapsed = isLeftNavCollapsed && level === 0;
  const showChildIndicator = level > 0;
  const contents = (
    <div
      className={classNames(
        "flex h-full grow items-center overflow-hidden whitespace-nowrap border-l-2 py-2 transition-[padding]",
        levelClassName,
        {
          "border-l-straps-accent-2": isActive,
          "border-l-transparent": !isActive,
          "pl-6": isTopLevelAndLeftNavCollapsed,
        }
      )}
    >
      {showChildIndicator && (
        <div className="relative mr-2.5">
          <ChildIndicator />
        </div>
      )}
      {iconName && (
        <Icon
          name={iconName}
          size="small"
          color={textColor}
          className={classNames("shrink-0", {
            "mr-2.5": !isTopLevelAndLeftNavCollapsed,
          })}
        />
      )}
      {!isTopLevelAndLeftNavCollapsed &&
        (titleElement ?? (
          <LeftNavItemRowTitle
            disabled={disabled}
            isChainActive={isChainActive}
          >
            {title}
          </LeftNavItemRowTitle>
        ))}
      {hasChildren && !isTopLevelAndLeftNavCollapsed && (
        <div className="mr-2.5 flex items-center justify-center outline-straps-hyperlink-hover">
          <Icon
            size="small"
            name={"caret-right"}
            className={classNames("!h-2.5 transition-transform", {
              "rotate-90": isExpanded,
            })}
            color={caretColor}
          />
        </div>
      )}
    </div>
  );

  const className = classNames(
    "flex cursor-pointer items-center py-2 pr-3 outline-straps-hyperlink-hover hover:bg-straps-positive-bg",
    {
      "bg-straps-positive-bg/40": isActive,
      "h-[54px]": level === 0,
      "h-[42px]": level > 0,
      "pointer-events-none": disabled,
    }
  );
  const style = {
    height: getHeight({ level }),
  };

  // Parents only toggle expand when clicked
  if (hasChildren) {
    return (
      <ClickableDiv
        aria-label={`Expand ${title} section`}
        onClick={(e) => {
          e.preventDefault();
          setExpanded?.(!isExpanded);
          onClick?.();
        }}
        className={className}
        style={style}
      >
        {contents}
      </ClickableDiv>
    );
  }

  if (!path) {
    return (
      <ClickableDiv
        aria-label={`${title} action`}
        onClick={(e) => {
          e.preventDefault();
          onClick?.();
        }}
        className={className}
        style={style}
      >
        {contents}
      </ClickableDiv>
    );
  }

  // Leaf nodes refer to a url
  return (
    <LinkWithQuery
      to={path}
      queries={persistQueries}
      aria-current={isActive ? "page" : undefined}
      data-testid={`left-nav-link-${path}`}
      className={className}
      style={style}
      onClick={onClick}
    >
      {contents}
    </LinkWithQuery>
  );
};
